import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Activity, TrendingUp, TrendingDown } from "lucide-react";
import type { RecruiterActivityData } from "@/hooks/dashboard/useDashboardKPIs";
import { KPITooltip } from "./KPITooltip";

interface RecruiterActivityTableProps {
	data: RecruiterActivityData[];
	tooltip?: string;
}

export function RecruiterActivityTable({ data, tooltip }: RecruiterActivityTableProps) {
	const { t } = useTranslation();

	if (data.length === 0)
		return (
			<Card>
				<CardHeader className="pb-2">
					<div className="flex items-center gap-2">
						<Activity className="h-5 w-5 text-muted-foreground" />
						<CardTitle className="text-lg">{t("dashboard.kpi.recruiterActivity")}</CardTitle>
						{tooltip && <KPITooltip text={tooltip} />}
					</div>
				</CardHeader>
				<CardContent>
					<div className="text-muted-foreground text-sm py-2">
						{t("dashboard.kpi.noData")}
					</div>
				</CardContent>
			</Card>
		);

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center gap-2">
					<Activity className="h-5 w-5 text-muted-foreground" />
					<CardTitle className="text-lg">{t("dashboard.kpi.recruiterActivity")}</CardTitle>
					{tooltip && <KPITooltip text={tooltip} />}
				</div>
			</CardHeader>
			<CardContent>
				<div className="overflow-x-auto">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>{t("dashboard.kpi.recruiterName")}</TableHead>
								<TableHead className="text-right">{t("dashboard.kpi.activities")}</TableHead>
								<TableHead className="text-right">
									<div className="flex items-center justify-end gap-1">
										<TrendingUp className="h-3 w-3 text-green-600" />
										{t("dashboard.kpi.progressions")}
									</div>
								</TableHead>
								<TableHead className="text-right">
									<div className="flex items-center justify-end gap-1">
										<TrendingDown className="h-3 w-3 text-red-600" />
										{t("dashboard.kpi.regressions")}
									</div>
								</TableHead>
								<TableHead className="text-right">{t("dashboard.kpi.screenings", "Screenings")}</TableHead>
								<TableHead className="text-right">{t("dashboard.kpi.interviews", "Interviews")}</TableHead>
								<TableHead className="text-right">{t("dashboard.kpi.hiresCount")}</TableHead>
								<TableHead className="text-right">{t("dashboard.kpi.refusals", "Refusals")}</TableHead>
								<TableHead className="text-right">{t("dashboard.kpi.conversionRate")}</TableHead>
								<TableHead className="text-right">{t("dashboard.kpi.avgResponseTime")}</TableHead>
								<TableHead>{t("dashboard.kpi.mostActiveStage")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{data.map((recruiter) => (
								<TableRow key={recruiter.id}>
									<TableCell className="font-medium">{recruiter.name}</TableCell>
									<TableCell className="text-right">
										<Badge variant="secondary">{recruiter.totalActivities}</Badge>
									</TableCell>
									<TableCell className="text-right text-green-600 font-medium">
										{recruiter.progressions}
									</TableCell>
									<TableCell className="text-right text-red-600 font-medium">
										{recruiter.regressions}
									</TableCell>
									<TableCell className="text-right text-sky-600 font-medium">
										{recruiter.screenings}
									</TableCell>
									<TableCell className="text-right text-teal-600 font-medium">
										{recruiter.interviews}
									</TableCell>
									<TableCell className="text-right font-semibold">
										{recruiter.hires}
									</TableCell>
									<TableCell className="text-right text-muted-foreground">
										{recruiter.refusals}
									</TableCell>
									<TableCell className="text-right">
										<span className={recruiter.conversionRate > 0 ? "text-green-600 font-medium" : "text-muted-foreground"}>
											{recruiter.conversionRate}%
										</span>
									</TableCell>
									<TableCell className="text-right text-muted-foreground">
										{recruiter.avgResponseTime !== null
											? `${recruiter.avgResponseTime}d`
											: "—"}
									</TableCell>
									<TableCell>
										{recruiter.mostActiveStage && (
											<Badge variant="outline">
												{t(`dashboard.kpi.stages.${recruiter.mostActiveStage}`, recruiter.mostActiveStage)}
											</Badge>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>

				{/* Summary Stats */}
				<div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
					<div>
						<div className="text-muted-foreground">{t("dashboard.kpi.totalActivities")}</div>
						<div className="text-2xl font-semibold">
							{data.reduce((sum, r) => sum + r.totalActivities, 0)}
						</div>
					</div>
					<div>
						<div className="text-muted-foreground">{t("dashboard.kpi.totalProgressions")}</div>
						<div className="text-2xl font-semibold text-green-600">
							{data.reduce((sum, r) => sum + r.progressions, 0)}
						</div>
					</div>
					<div>
						<div className="text-muted-foreground">{t("dashboard.kpi.totalHires")}</div>
						<div className="text-2xl font-semibold text-blue-500">
							{data.reduce((sum, r) => sum + r.hires, 0)}
						</div>
					</div>
					<div>
						<div className="text-muted-foreground">{t("dashboard.kpi.avgConversionRate")}</div>
						<div className="text-2xl font-semibold">
							{data.length > 0
								? Math.round(data.reduce((sum, r) => sum + r.conversionRate, 0) / data.length)
								: 0}%
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}
